import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './use-api-client.js';

// Slack connection of a server (ADR-039 section 8)

export function useSlack(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['slack', serverId], queryFn: () => api.slack.status(serverId) });
}

/** Opens Slack's consent page; the webapp's /slack/callback completes it */
export function useConnectSlack(serverId: string) {
  const api = useApiClient();
  return useMutation({
    mutationFn: () => api.slack.connect(serverId),
    onSuccess: ({ url }) => { window.location.assign(url); },
  });
}

export function useDisconnectSlack(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.slack.disconnect(serverId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['slack', serverId] }); },
  });
}

/** One conversation aidev kept, to answer it (ADR-039 step 8) */
export function useSlackThread(serverId: string, group: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['slack-thread', serverId, group], queryFn: () => api.slack.thread(serverId, group) });
}

/** A draft of the reply in the operator's voice, on demand: nothing is sent */
export function useSlackDraft(serverId: string, group: string) {
  const api = useApiClient();
  return useMutation({ mutationFn: () => api.slack.draft(serverId, group) });
}

/** Sends the reply under the operator's name; the line of To do then waits for an answer */
export function useSlackReply(serverId: string, group: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => api.slack.reply(serverId, group, text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inbox', serverId] });
      void qc.invalidateQueries({ queryKey: ['inbox-hidden', serverId] });
    },
  });
}
